import {
  CalendarEventAvailability,
  CalendarEventDetails,
  CalendarEventFormFields,
  CalendarEventGuestNotification,
  CalendarEventOccurrenceTarget,
  CalendarEventRef,
  CreateCalendarEventInput,
  UpdateCalendarEventInput,
} from '@griply/types';
import {
  formatDateKey,
  formatNaiveCalendarDateTime,
  getCalendarTaskStartTime,
  hasCalendarChanged,
  isPlainTextUnchangedFromHtml,
  plainTextToHtml,
} from '@griply/utils';
import { subDays } from 'date-fns';

const formatBoundary = (
  allDay: boolean,
  date: Date,
  timeOfDayMs: number | null,
) =>
  allDay
    ? formatDateKey(date)
    : formatNaiveCalendarDateTime(date, timeOfDayMs ?? 0);

const contentFields = (state: CalendarEventFormFields) => ({
  title: state.title,
  location: state.location || null,
  allDay: state.allDay,
  start: formatBoundary(state.allDay, state.startDate, state.startTime),
  end: formatBoundary(state.allDay, state.endDate, state.endTime),
  // display-only guest fields (name, responseStatus) stay off the wire. `optional` is only
  // carried when the guest actually has one — a guest the user just typed has none, and
  // projecting it anyway would put an own key holding undefined on the object, which the
  // callable encoder sends as an explicit null.
  guests: state.guests.map(({ email, optional }) => ({
    email,
    ...(optional === undefined ? {} : { optional }),
  })),
  // both, deliberately: `busy` is all a deploy predating showAs can read, and it derives the same value
  showAs: state.availability,
  busy: state.availability !== CalendarEventAvailability.Free,
  // null reminders = calendar defaults; omit so the provider keeps them
  ...(state.reminders ? { reminders: state.reminders } : {}),
});

export const mapFormStateToCreateInput = (
  state: CalendarEventFormFields,
  displayTimeZone: string,
): CreateCalendarEventInput => ({
  connectionId: state.calendar!.connectionId,
  calendarId: state.calendar!.calendarId,
  ...contentFields(state),
  // always on create: there is no existing event whose conference this could disturb
  videoCall: state.videoCall,
  description: plainTextToHtml(state.description) || null,
  rrule: state.rrule,
  // No override means the form's wall clock is the grid's zone — the calendar's would re-read it.
  timeZone: state.timeZone ?? displayTimeZone,
});

export const mapFormStateToUpdateInput = (
  state: CalendarEventFormFields,
  details: CalendarEventDetails,
  occurrenceTarget: CalendarEventOccurrenceTarget,
  // Required so a new call site decides rather than inheriting "mail everyone" by omission
  guestNotification: CalendarEventGuestNotification | null,
): UpdateCalendarEventInput => ({
  // The event's CURRENT home, i.e. the lookup — where the user wants it travels as the target below.
  connectionId: details.connectionId,
  calendarId: details.calendarId,
  eventId: details.id,
  editScope: occurrenceTarget,
  ...(guestNotification ? { sendUpdates: guestNotification } : {}),
  // Only a real change travels — restating the current calendar would cost a provider round-trip.
  ...(hasCalendarChanged(state.calendar, details)
    ? {
        targetConnectionId: state.calendar!.connectionId,
        targetCalendarId: state.calendar!.calendarId,
      }
    : {}),
  ...contentFields(state),
  // A provider description is rich text. The form edits a plain-text projection of it, so an
  // untouched description is compared against that same projection and stays off the patch
  // entirely — its inline marks only die when the user actually rewrites the text, and even
  // then the paragraph and list structure travels back as markup.
  ...(isPlainTextUnchangedFromHtml(state.description, details.description)
    ? {}
    : { description: plainTextToHtml(state.description) || null }),
  // A recurring instance carries no rrule of its own (the rule lives on the series master),
  // so the form holds null for an event that very much repeats. Echoing that back would ask
  // the provider to strip the recurrence — only a real change to the rule travels.
  ...(state.rrule !== details.rrule ? { rrule: state.rrule } : {}),
  // Outlook refuses `videoCall: false` even with no conference; an armed swap must still restate it
  ...(state.videoCall !== details.videoCall || state.replaceVideoCall
    ? { videoCall: state.videoCall }
    : {}),
  // Only when the user actually asked to swap the conference. Absent is what tells the backend to keep
  // whatever the event has — sending false would be harmless but says nothing, and this patch is the one
  // place where an unnecessary key has ever cost us a link.
  ...(state.replaceVideoCall ? { replaceVideoCall: true } : {}),
  // The edit form's clock is the event's own zone, never the grid's — so no display-zone default here.
  timeZone: state.timeZone,
});

export type MapEventTimesOptions = {
  allDay: boolean;
  start: Date;
  // absent on a zero-length grid event; collapses to a same-day block
  end: Date | null;
  occurrenceTarget: CalendarEventOccurrenceTarget;
  // the zone the grid the user dragged in renders — see below
  timeZone: string;
  // required so a new call site answers the question; null leaves the provider's own default
  guestNotification: CalendarEventGuestNotification | null;
};

// Drag/resize patch: boundaries only. Every content field stays absent so the
// provider leaves title, guests, reminders and recurrence exactly as they were.
//
// `timeZone` is not the event's own zone but the GRID's: the boundaries here are Dates read
// back as local wall-clock, so the zone that makes them mean anything is the one the user was
// looking at. Stating it is what makes a drag land where it was dropped — without it the
// backend reads those times in the calendar's zone, which is a different instant whenever the
// two differ. The trade-off is that dragging an event anchored to some third zone re-anchors
// it to the grid's; that is visible and matches where the user put it, where the alternative
// silently shifts the event by the offset between the zones.
export const mapEventTimesToUpdateInput = (
  eventRef: CalendarEventRef,
  {
    allDay,
    start,
    end,
    occurrenceTarget,
    timeZone,
    guestNotification,
  }: MapEventTimesOptions,
): UpdateCalendarEventInput => {
  // a grid all-day end is exclusive; the wire boundary is inclusive
  const resolvedEnd = allDay ? (end ? subDays(end, 1) : start) : end ?? start;

  return {
    ...eventRef,
    editScope: occurrenceTarget,
    ...(guestNotification ? { sendUpdates: guestNotification } : {}),
    allDay,
    start: formatBoundary(allDay, start, getCalendarTaskStartTime(start)),
    end: formatBoundary(
      allDay,
      resolvedEnd,
      getCalendarTaskStartTime(resolvedEnd),
    ),
    // an all-day event covers whole calendar days and carries no zone
    timeZone: allDay ? null : timeZone,
  };
};
